// API клиент для конфигуратора recviz_v2
import { apiClient } from './client'

// Типы для recviz
export interface RecvizPeriod {
  period: string
  label: string
}

export interface RecvizDepartment {
  // Идентификация
  id_otd: string
  period: string

  // Структура
  podr_name_full: string
  code_otd_name_full: string
  oid: string
  code_otd: string
  status: 'active' | 'closed'

  // Реквизиты (привязки)
  fil_code: number | null
  fil_name: string | null
  serv_code: number | null
  serv_name: string | null
  // Хирургическая активность
  is_surgical: boolean | null
  auto_is_surgical: boolean | null
  // Детальный тип профиля
  profil_type_detailed: string | null
  auto_profil_type_detailed: string | null

  // Коечный фонд
  kf_total_fact: number | null
  kf_total_prikaz: number | null
  planned_cases: number | null

  // Вычисляемые показатели (dual-field паттерн)
  // ИКЗ (Интегральный Коэффициент Затратоёмкости)
  calculated_ikz: number | null
  manual_ikz: number | null
  traffic_light_ikz: 'none' | 'green' | 'orange' | 'red'

  // СДПП (Средняя Длительность Пребывания Пациента)
  calculated_sdpp: number | null
  manual_sdpp: number | null
  traffic_light_sdpp: 'none' | 'green' | 'orange' | 'red'

  // Работа койки (дней в году) - нормативный показатель из приказа
  normative_bed_work_days: number | null
  manual_bed_work_days: number | null
  traffic_light_bed_work: 'none' | 'green' | 'orange' | 'red'
  norm_source: string | null // Источник норматива (приказ, дата)

  // Доля плановых случаев (% плановых vs экстренных)
  calculated_pct_planned: number | null // 100% для ДС, 50% для КС
  manual_pct_planned: number | null

  // Warnings (опционально)
  warnings?: string[]
}

export interface RecvizSummary {
  total_departments: number
  departments_bound: number
  total_beds_fact: number
  total_beds_prikaz: number
}

export interface RecvizDataFilters {
  fil_code?: number
  serv_code?: number
}

export interface RecvizDataResponse {
  items: RecvizDepartment[]
  total: number
  period: string
}

// API функции
export const fetchRecvizPeriods = async (): Promise<RecvizPeriod[]> => {
  const response = await apiClient.get('/recviz/periods')
  // Backend returns array of {period, label} objects directly
  return response.data
}

export const fetchRecvizData = async (
  period: string,
  filters?: RecvizDataFilters
): Promise<RecvizDepartment[]> => {
  const response = await apiClient.get<RecvizDataResponse>('/recviz/data', {
    params: { period, ...filters }
  })
  return response.data.items
}

export const fetchRecvizSummary = async (
  period: string
): Promise<RecvizSummary> => {
  const response = await apiClient.get('/recviz/summary', {
    params: { period }
  })
  return response.data
}

// Типы для обновления
export interface RecvizDepartmentUpdate {
  fil_code?: number | null
  serv_code?: number | null
  is_surgical?: boolean | null
  profil_type_detailed?: string | null
  // Manual override для показателей
  manual_ikz?: number | null
  manual_sdpp?: number | null
  manual_bed_work_days?: number | null
}

export interface RecvizBatchUpdateRequest {
  updates: Record<string, RecvizDepartmentUpdate>
}

export interface RecvizBatchUpdateResponse {
  updated_count: number
  errors: string[]
}

// Обновление привязок отделений
export const updateRecvizData = async (
  period: string,
  updates: Record<string, RecvizDepartmentUpdate>
): Promise<RecvizBatchUpdateResponse> => {
  const response = await apiClient.patch<RecvizBatchUpdateResponse>(
    '/recviz/data',
    { updates },
    { params: { period } }
  )
  return response.data
}

// Типы для детализации расчёта показателей
export type IndicatorType = 'ikz' | 'sdpp' | 'bed_work' | 'pct_planned'

export interface PeriodRow {
  period: string // MMYY формат
  label: string // "Январь 2024"
  value: number
  cases: number
  included: boolean
  excluded_reason: string | null // причина исключения алгоритмом
  is_manual_toggle: boolean // был изменён пользователем
  manual_warning: string | null // предупреждение если включён вручную
}

export interface CalcDetailData {
  periods: PeriodRow[]
  annual_avg: number // среднегодовой
  max_monthly_value: number // максимальное значение месяца
  max_monthly_period: string // период с макс значением
  avg_max: number // среднемаксимальный
  benchmark: number | null // областной бенчмарк (может отсутствовать)
  planned_value: number // итоговое плановое значение
  planned_source: 'our' | 'average' // источник планового значения
}

export interface PeriodToggleRequest {
  period: string
  included: boolean
}

// Backend response types (match CalcResultSchema)
interface BackendPeriodDetail {
  period: string
  value: number | null
  cases_count: number
  included: boolean
  exclusion_reason: string | null
}

interface BackendCalcResponse {
  periods: BackendPeriodDetail[]
  summary: {
    annual_avg: number | null
    max_included: number | null
    max_period: string | null
    average_maximum: number | null
    benchmark: number | null
    planned_value: number | null
  }
}

const MONTH_NAMES: Record<string, string> = {
  '01': 'Январь', '02': 'Февраль', '03': 'Март', '04': 'Апрель',
  '05': 'Май', '06': 'Июнь', '07': 'Июль', '08': 'Август',
  '09': 'Сентябрь', '10': 'Октябрь', '11': 'Ноябрь', '12': 'Декабрь',
}

// Map backend response to frontend CalcDetailData
function mapCalcResponse(data: BackendCalcResponse): CalcDetailData {
  const periods: PeriodRow[] = data.periods.map(p => {
    const mm = p.period.slice(0, 2)
    const yy = p.period.slice(2, 4)
    return {
      period: p.period,
      label: `${MONTH_NAMES[mm] || mm} 20${yy}`,
      value: p.value ?? 0,
      cases: p.cases_count,
      included: p.included,
      excluded_reason: p.exclusion_reason,
      is_manual_toggle: false,
      manual_warning: null,
    }
  })

  const s = data.summary
  const isOur = s.average_maximum != null && s.benchmark != null && s.planned_value === s.average_maximum

  return {
    periods,
    annual_avg: s.annual_avg ?? 0,
    max_monthly_value: s.max_included ?? 0,
    max_monthly_period: s.max_period ?? '',
    avg_max: s.average_maximum ?? 0,
    benchmark: s.benchmark,
    planned_value: s.planned_value ?? 0,
    planned_source: isOur ? 'our' : 'average',
  }
}

// Получить детализацию расчёта показателя
export const fetchCalculationDetail = async (
  departmentId: string,
  indicatorType: IndicatorType,
  year: number
): Promise<CalcDetailData> => {
  const response = await apiClient.get<BackendCalcResponse>(
    '/recviz/calculation-detail',
    {
      params: { department_id: departmentId, indicator: indicatorType, year }
    }
  )
  return mapCalcResponse(response.data)
}

// Backend response for period-settings (wraps CalcResultSchema in {success, calculation})
interface BackendPeriodSettingsResponse {
  success: boolean
  calculation: BackendCalcResponse
}

// Изменить включение периода в расчёт
export const togglePeriodInclusion = async (
  departmentId: string,
  indicatorType: IndicatorType,
  _year: number,
  periodData: PeriodToggleRequest
): Promise<CalcDetailData> => {
  // Backend expects PeriodSettingsRequest as body (department_id, indicator_type, period, included)
  const response = await apiClient.patch<BackendPeriodSettingsResponse>(
    '/recviz/period-settings',
    {
      department_id: departmentId,
      indicator_type: indicatorType,
      period: periodData.period,
      included: periodData.included,
    }
  )
  return mapCalcResponse(response.data.calculation)
}

// Получить настройки порогов светофора
export const fetchThresholdSettings = async (): Promise<ThresholdSettings> => {
  const response = await apiClient.get<ThresholdSettings>('/recviz/threshold-settings')
  return response.data
}

// Типы для threshold settings
export interface ThresholdSettings {
  threshold_green: number
  threshold_orange: number
  indicator_overrides?: Record<string, { threshold_green: number; threshold_orange: number }>
}

// Типы для manual value
export interface ManualValueRequest {
  department_id: string
  indicator_type: IndicatorType
  value: number | null
}

export interface ManualValueResponse {
  status: 'updated' | 'deleted' | 'not_found'
  department_id: string
  indicator_type: string
  value?: number
}

// Установить или сбросить manual value для показателя
export const updateManualValue = async (
  request: ManualValueRequest
): Promise<ManualValueResponse> => {
  const response = await apiClient.patch<ManualValueResponse>(
    '/recviz/manual-value',
    request
  )
  return response.data
}

// 4.5.2 Generate Recviz (справочник реквизитов)

export interface GenerateRecvizRequest {
  period: string
  export_to_ch: boolean
}

export interface RecvizRow {
  ot_per: string
  podr_name_full: string | null
  code_otd_name_full: string | null
  id_otd: string
  fil_name: string | null
  serv_name: string | null
  kf_total_fact: number | null
  kf_total_prikaz: number | null
  cases_plan_gen: number | null
  [key: string]: any
}

export interface ExportStatus {
  success: boolean
  rows_exported?: number
  table?: string
  error?: string
}

export interface GenerateRecvizResponse {
  success: boolean
  rows_count: number
  period: string
  preview: RecvizRow[]
  export_status: ExportStatus | null
}

// Формирование справочника реквизитов (один период)
export const generateRecviz = async (
  request: GenerateRecvizRequest
): Promise<GenerateRecvizResponse> => {
  const response = await apiClient.post<GenerateRecvizResponse>(
    '/recviz/generate-recviz',
    request
  )
  return response.data
}

// Multi-period generation

export interface GenerateRecvizMultiRequest {
  periods: string[]
  export_to_ch: boolean
}

export interface PeriodResult {
  period: string
  rows_count: number
  fallback: boolean
  export_status: ExportStatus | null
}

export interface GenerateRecvizMultiResponse {
  success: boolean
  total_rows: number
  results: PeriodResult[]
  preview: RecvizRow[]
}

export const generateRecvizMulti = async (
  request: GenerateRecvizMultiRequest
): Promise<GenerateRecvizMultiResponse> => {
  const response = await apiClient.post<GenerateRecvizMultiResponse>(
    '/recviz/generate-recviz-multi',
    request
  )
  return response.data
}
